import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../supabaseClient";
import {
  FaCheckCircle,
  FaEdit,
  FaExclamationTriangle,
  FaPlus,
  FaSearch,
  FaSyncAlt,
  FaTrash,
  FaTrashRestore,
  FaUserShield,
} from "react-icons/fa";
import Header from "../components/AdminHeader";

const STATUS_OPTIONS = ["Scheduled", "Attended", "Cancelled"];

function formatDisplayDate(dateString) {
  if (!dateString) return "—";
  const d = new Date(`${dateString}T12:00:00`);
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function formatTimeDisplay(timeString) {
  if (!timeString) return "—";
  const [hours, minutes] = String(timeString).split(":");
  return `${hours}:${minutes}`;
}

function getStatusBadgeClasses(status) {
  if (status === "Attended") {
    return "border-emerald-400/20 bg-emerald-500/10 text-emerald-100";
  }
  if (status === "Cancelled") {
    return "border-red-400/20 bg-red-500/10 text-red-100";
  }
  return "border-cyan-300/20 bg-cyan-400/10 text-cyan-100";
}

export default function Admin() {
  const navigate = useNavigate();

  const [session, setSession] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);

  const [rows, setRows] = useState([]);
  const [shows, setShows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showsLoading, setShowsLoading] = useState(true);
  const [pageError, setPageError] = useState("");
  const [search, setSearch] = useState("");
  const [savingCode, setSavingCode] = useState("");
  const [toast, setToast] = useState("");

  const [showModalOpen, setShowModalOpen] = useState(false);
  const [editingShow, setEditingShow] = useState(null);
  const [showFormLoading, setShowFormLoading] = useState(false);
  const [showForm, setShowForm] = useState({
    show_name: "",
    show_date: "",
    show_time: "",
  });

  const [cancelShowModalOpen, setCancelShowModalOpen] = useState(false);
  const [showToCancel, setShowToCancel] = useState(null);
  const [cancelShowLoading, setCancelShowLoading] = useState(false);

  const [isBinHovering, setIsBinHovering] = useState(false);
  const [draggingLabel, setDraggingLabel] = useState("");

  const initAdminAccess = useCallback(async () => {
    try {
      setAuthLoading(true);

      const {
        data: { session: activeSession },
      } = await supabase.auth.getSession();

      if (!activeSession?.user) {
        setSession(null);
        setIsAdmin(false);
        setAuthLoading(false);
        navigate("/login", { replace: true });
        return;
      }

      setSession(activeSession);

      const { data: profile, error: profileError } = await supabase
        .from("profiles")
        .select("id, ADMIN")
        .eq("id", activeSession.user.id)
        .maybeSingle();

      if (profileError || !profile || profile.ADMIN !== true) {
        setIsAdmin(false);
        setAuthLoading(false);
        navigate("/home", { replace: true });
        return;
      }

      setIsAdmin(true);
      setAuthLoading(false);
    } catch (err) {
      console.error("Admin access init failed:", err);
      setSession(null);
      setIsAdmin(false);
      setAuthLoading(false);
      navigate("/home", { replace: true });
    }
  }, [navigate]);

  const fetchBookings = useCallback(async () => {
    setLoading(true);
    setPageError("");
    setToast("");

    const { data, error } = await supabase
      .from("seat_bookings")
      .select(
        "id, show_id, email, roblox_username, seat_id, show_date, booking_reference, ticket_status, created_at, shows(show_name, show_time)"
      )
      .order("created_at", { ascending: false });

    if (error) {
      console.error("Failed to fetch bookings:", error);
      setRows([]);
      setPageError("Could not load booking records.");
      setLoading(false);
      return;
    }

    const grouped = new Map();

    for (const row of data || []) {
      const key = row.booking_reference || `NOREF-${row.id}`;

      if (!grouped.has(key)) {
        grouped.set(key, {
          booking_reference: row.booking_reference || "—",
          show_id: row.show_id || null,
          show_name: row.shows?.show_name || "",
          show_time: row.shows?.show_time || "",
          email: row.email || "",
          roblox_username: row.roblox_username || "",
          show_date: row.show_date || "",
          ticket_status: row.ticket_status || "Scheduled",
          created_at: row.created_at || null,
          seats: [],
          capacity: 0,
        });
      }

      const booking = grouped.get(key);
      booking.seats.push(row.seat_id);
      booking.capacity += 1;

      if (
        row.created_at &&
        (!booking.created_at ||
          new Date(row.created_at) > new Date(booking.created_at))
      ) {
        booking.created_at = row.created_at;
      }
    }

    const groupedRows = Array.from(grouped.values()).sort((a, b) => {
      const aTime = a.created_at ? new Date(a.created_at).getTime() : 0;
      const bTime = b.created_at ? new Date(b.created_at).getTime() : 0;
      return bTime - aTime;
    });

    setRows(groupedRows);
    setLoading(false);
  }, []);

  const fetchShows = useCallback(async () => {
    setShowsLoading(true);

    const { data, error } = await supabase
      .from("shows")
      .select("id, show_name, show_date, show_time, enabled, created_at")
      .order("show_date", { ascending: true })
      .order("show_time", { ascending: true });

    if (error) {
      console.error("Failed to fetch shows:", error);
      setShows([]);
      setShowsLoading(false);
      return;
    }

    setShows(data || []);
    setShowsLoading(false);
  }, []);

  useEffect(() => {
    initAdminAccess();
  }, [initAdminAccess]);

  useEffect(() => {
    if (!authLoading && isAdmin) {
      fetchBookings();
      fetchShows();
    }
  }, [authLoading, isAdmin, fetchBookings, fetchShows]);

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;

    return rows.filter((row) => {
      return (
        row.email.toLowerCase().includes(q) ||
        row.roblox_username.toLowerCase().includes(q) ||
        row.booking_reference.toLowerCase().includes(q) ||
        row.show_date.toLowerCase().includes(q) ||
        row.show_name.toLowerCase().includes(q) ||
        row.seats.join(", ").toLowerCase().includes(q)
      );
    });
  }, [rows, search]);

  const openCreateShowModal = () => {
    setEditingShow(null);
    setShowForm({
      show_name: "",
      show_date: "",
      show_time: "",
    });
    setShowModalOpen(true);
  };

  const openEditShowModal = (show) => {
    setEditingShow(show);
    setShowForm({
      show_name: show.show_name || "",
      show_date: show.show_date || "",
      show_time: show.show_time ? show.show_time.slice(0, 5) : "",
    });
    setShowModalOpen(true);
  };

  const closeShowModal = () => {
    if (showFormLoading) return;
    setShowModalOpen(false);
    setEditingShow(null);
    setShowForm({
      show_name: "",
      show_date: "",
      show_time: "",
    });
  };

  const openCancelShowModal = (show) => {
    setShowToCancel(show);
    setCancelShowModalOpen(true);
  };

  const closeCancelShowModal = () => {
    if (cancelShowLoading) return;
    setShowToCancel(null);
    setCancelShowModalOpen(false);
  };

  const handleShowFormChange = (e) => {
    const { name, value } = e.target;
    setShowForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSaveShow = async (e) => {
    e.preventDefault();

    if (!showForm.show_name.trim() || !showForm.show_date || !showForm.show_time) {
      setPageError("Please complete all show fields.");
      return;
    }

    setShowFormLoading(true);
    setPageError("");
    setToast("");

    if (editingShow) {
      const { error } = await supabase
        .from("shows")
        .update({
          show_name: showForm.show_name.trim(),
          show_date: showForm.show_date,
          show_time: showForm.show_time,
        })
        .eq("id", editingShow.id);

      if (error) {
        console.error("Failed to update show:", error);
        setPageError("Could not update show.");
        setShowFormLoading(false);
        return;
      }

      setToast("Show updated.");
    } else {
      const { error } = await supabase.from("shows").insert({
        show_name: showForm.show_name.trim(),
        show_date: showForm.show_date,
        show_time: showForm.show_time,
        enabled: true,
      });

      if (error) {
        console.error("Failed to create show:", error);
        setPageError("Could not create show.");
        setShowFormLoading(false);
        return;
      }

      setToast("Show created.");
    }

    setShowFormLoading(false);
    closeShowModal();
    await fetchShows();
  };

  const handleToggleShow = async (show) => {
    const { error } = await supabase
      .from("shows")
      .update({ enabled: !show.enabled })
      .eq("id", show.id);

    if (error) {
      console.error("Failed to toggle show:", error);
      setPageError("Could not update show status.");
      return;
    }

    setToast(`Show ${show.enabled ? "disabled" : "enabled"}.`);
    await fetchShows();
  };

  const moveBookingToRecycleBin = async (booking) => {
    if (!booking?.booking_reference || !session?.user?.id) return;

    setSavingCode(booking.booking_reference);
    setPageError("");
    setToast("");

    const { data: bookingRows, error: fetchError } = await supabase
      .from("seat_bookings")
      .select("*")
      .eq("booking_reference", booking.booking_reference)
      .order("created_at", { ascending: true });

    if (fetchError) {
      console.error("Failed to fetch booking rows for recycle bin:", fetchError);
      setPageError("Could not prepare booking for recycle bin.");
      setSavingCode("");
      return;
    }

    const showId = bookingRows?.[0]?.show_id ?? null;
    let linkedShow = null;

    if (showId) {
      const { data: showData, error: showError } = await supabase
        .from("shows")
        .select("*")
        .eq("id", showId)
        .maybeSingle();

      if (showError) {
        console.error("Failed to fetch linked show for booking recycle bin:", showError);
        setPageError("Could not prepare linked show for recycle bin.");
        setSavingCode("");
        return;
      }

      linkedShow = showData || null;
    }

    const recyclePayload = {
      bookings: bookingRows || [],
      show: linkedShow,
    };

    const { error: recycleError } = await supabase.from("recyclebin").insert({
      item_type: "booking",
      source_table: "seat_bookings",
      source_key: booking.booking_reference,
      title: `${booking.roblox_username || booking.email || "Booking"} • ${booking.booking_reference}`,
      payload: recyclePayload,
      deleted_by: session.user.id,
    });

    if (recycleError) {
      console.error("Failed to insert booking into recycle bin:", recycleError);
      setPageError("Could not move booking to recycle bin.");
      setSavingCode("");
      return;
    }

    const { error: deleteError } = await supabase
      .from("seat_bookings")
      .delete()
      .eq("booking_reference", booking.booking_reference);

    if (deleteError) {
      console.error("Failed to delete booking after recycle insert:", deleteError);
      setPageError("Booking was copied to recycle bin, but removal failed.");
      setSavingCode("");
      return;
    }

    setToast(`Booking ${booking.booking_reference} moved to recycle bin.`);
    setSavingCode("");
    await fetchBookings();
  };

  const moveShowToRecycleBin = async (show) => {
    if (!show?.id || !session?.user?.id) return;

    setSavingCode(`show-${show.id}`);
    setPageError("");
    setToast("");

    const { data: linkedBookings, error: bookingsFetchError } = await supabase
      .from("seat_bookings")
      .select("*")
      .eq("show_id", show.id)
      .order("created_at", { ascending: true });

    if (bookingsFetchError) {
      console.error("Failed to fetch show bookings for recycle bin:", bookingsFetchError);
      setPageError("Could not prepare show for recycle bin.");
      setSavingCode("");
      return;
    }

    const recyclePayload = {
      show,
      bookings: linkedBookings || [],
    };

    const { error: recycleError } = await supabase.from("recyclebin").insert({
      item_type: "show",
      source_table: "shows",
      source_key: String(show.id),
      title: `${show.show_name} • ${formatDisplayDate(show.show_date)} • ${formatTimeDisplay(show.show_time)}`,
      payload: recyclePayload,
      deleted_by: session.user.id,
    });

    if (recycleError) {
      console.error("Failed to insert show into recycle bin:", recycleError);
      setPageError("Could not move show to recycle bin.");
      setSavingCode("");
      return;
    }

    if ((linkedBookings || []).length > 0) {
      const { error: deleteBookingsError } = await supabase
        .from("seat_bookings")
        .delete()
        .eq("show_id", show.id);

      if (deleteBookingsError) {
        console.error("Failed to remove show bookings:", deleteBookingsError);
        setPageError("Show saved to recycle bin, but its bookings could not be removed.");
        setSavingCode("");
        return;
      }
    }

    const { error: deleteShowError } = await supabase
      .from("shows")
      .delete()
      .eq("id", show.id);

    if (deleteShowError) {
      console.error("Failed to delete show after recycle insert:", deleteShowError);
      setPageError("Show was copied to recycle bin, but removal failed.");
      setSavingCode("");
      return;
    }

    setToast(`Show "${show.show_name}" moved to recycle bin.`);
    setSavingCode("");
    await fetchShows();
    await fetchBookings();
  };

  const handleDeleteShow = async (show) => {
    const confirmed = window.confirm(
      `Move "${show.show_name}" on ${formatDisplayDate(show.show_date)} at ${formatTimeDisplay(show.show_time)} to recycle bin?`
    );

    if (!confirmed) return;
    await moveShowToRecycleBin(show);
  };

  const handleDeleteBooking = async (booking) => {
    const confirmed = window.confirm(
      `Move booking ${booking.booking_reference} to recycle bin?`
    );

    if (!confirmed) return;
    await moveBookingToRecycleBin(booking);
  };

  const handleCancelShow = async () => {
    if (!showToCancel) return;

    setCancelShowLoading(true);
    setPageError("");
    setToast("");

    const { data: bookingsForShow, error: bookingsError } = await supabase
      .from("seat_bookings")
      .select(
        "id, show_id, email, roblox_username, seat_id, booking_reference, ticket_status"
      )
      .eq("show_id", showToCancel.id)
      .order("booking_reference", { ascending: true });

    if (bookingsError) {
      console.error("Failed to fetch show bookings:", bookingsError);
      setPageError("Could not load bookings for this show.");
      setCancelShowLoading(false);
      return;
    }

    const groupedBookings = new Map();

    for (const booking of bookingsForShow || []) {
      const key = booking.booking_reference || `NOREF-${booking.id}`;

      if (!groupedBookings.has(key)) {
        groupedBookings.set(key, {
          email: booking.email || "",
          roblox_username: booking.roblox_username || "",
          booking_reference: booking.booking_reference || "—",
          seats: [],
          capacity: 0,
        });
      }

      const grouped = groupedBookings.get(key);
      grouped.seats.push(booking.seat_id);
      grouped.capacity += 1;
    }

    for (const booking of groupedBookings.values()) {
      if (!booking.email) continue;

      try {
        await fetch("/api/send-booking-status-email", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            to: booking.email,
            robloxUsername: booking.roblox_username,
            peopleAttending: booking.capacity,
            bookingReference: booking.booking_reference,
            ticketStatus: "Cancelled",
            showDate: showToCancel.show_date,
            showName: showToCancel.show_name,
            showTime: showToCancel.show_time,
            seats: booking.seats,
            emailType: "show_cancelled",
          }),
        });
      } catch (error) {
        console.error(
          `Failed to send cancellation email for ${booking.booking_reference}:`,
          error,
        );
      }
    }

    const { error: deleteBookingsError } = await supabase
      .from("seat_bookings")
      .delete()
      .eq("show_id", showToCancel.id);

    if (deleteBookingsError) {
      console.error("Failed to delete cancelled show bookings:", deleteBookingsError);
      setPageError("Could not delete bookings for the cancelled show.");
      setCancelShowLoading(false);
      return;
    }

    const { error: deleteShowError } = await supabase
      .from("shows")
      .delete()
      .eq("id", showToCancel.id);

    if (deleteShowError) {
      console.error("Failed to delete cancelled show:", deleteShowError);
      setPageError("Could not delete the cancelled show.");
      setCancelShowLoading(false);
      return;
    }

    setCancelShowLoading(false);
    closeCancelShowModal();
    setToast(`Show "${showToCancel.show_name}" cancelled and removed.`);
    await fetchShows();
    await fetchBookings();
  };

  const handleStatusChange = async (booking, nextStatus) => {
    if (!booking?.booking_reference) return;

    setSavingCode(booking.booking_reference);
    setToast("");
    setPageError("");

    const { error: updateError } = await supabase
      .from("seat_bookings")
      .update({
        ticket_status: nextStatus,
      })
      .eq("booking_reference", booking.booking_reference);

    if (updateError) {
      console.error("Failed to update booking status:", updateError);
      setPageError("Failed to update the booking status.");
      setSavingCode("");
      return;
    }

    try {
      await fetch("/api/send-booking-status-email", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          to: booking.email,
          robloxUsername: booking.roblox_username,
          peopleAttending: booking.capacity,
          bookingReference: booking.booking_reference,
          ticketStatus: nextStatus,
          showDate: booking.show_date,
          showName: booking.show_name,
          showTime: booking.show_time,
          seats: booking.seats,
        }),
      });
    } catch (error) {
      console.error("Failed to send status email:", error);
    }

    setToast(`Updated ${booking.booking_reference} to ${nextStatus}.`);
    setSavingCode("");
    await fetchBookings();
  };

  const handleDragStart = (payload, label) => (e) => {
    e.dataTransfer.setData("application/json", JSON.stringify(payload));
    e.dataTransfer.effectAllowed = "move";
    setDraggingLabel(label);
  };

  const handleDragEnd = () => {
    setIsBinHovering(false);
    setDraggingLabel("");
  };

  const handleBinDragOver = (e) => {
    e.preventDefault();
    setIsBinHovering(true);
    e.dataTransfer.dropEffect = "move";
  };

  const handleBinDragLeave = () => {
    setIsBinHovering(false);
  };

  const handleBinDrop = async (e) => {
    e.preventDefault();
    setIsBinHovering(false);

    try {
      const raw = e.dataTransfer.getData("application/json");
      if (!raw) return;

      const parsed = JSON.parse(raw);

      if (parsed?.type === "show" && parsed?.data) {
        await moveShowToRecycleBin(parsed.data);
      }

      if (parsed?.type === "booking" && parsed?.data) {
        await moveBookingToRecycleBin(parsed.data);
      }
    } catch (error) {
      console.error("Failed to process drop to recycle bin:", error);
      setPageError("Could not move dragged item to recycle bin.");
    } finally {
      setDraggingLabel("");
    }
  };

  if (authLoading) {
    return (
      <div className="grid min-h-screen place-items-center bg-[#08111f] text-white">
        Checking admin access...
      </div>
    );
  }

  if (!session || !isAdmin) {
    return null;
  }

  return (
    <>
      <Header />
      <div className="min-h-screen bg-[#08111f] text-white">
        <div className="pointer-events-none fixed inset-0">
          <div className="absolute inset-0 bg-[linear-gradient(135deg,#4a0454_0%,#3c2f73_35%,#255a86_68%,#12728a_100%)] opacity-90" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(255,255,255,0.08),transparent_26%),radial-gradient(circle_at_bottom_right,rgba(255,255,255,0.05),transparent_24%)]" />
          <div className="absolute inset-0 bg-black/30" />
        </div>

        <div className="relative mx-auto max-w-[1800px] px-4 py-5 sm:px-6 sm:py-8 lg:px-8 lg:py-10">
          <section className="mb-8 overflow-hidden rounded-[30px] border border-white/10 bg-[linear-gradient(135deg,rgba(86,24,113,0.45),rgba(39,78,125,0.45),rgba(18,111,135,0.40))] shadow-[0_20px_80px_rgba(0,0,0,0.32)] backdrop-blur-xl">
            <div className="grid gap-8 px-5 py-6 sm:px-8 sm:py-8 lg:grid-cols-[1.1fr_0.9fr] lg:px-10 lg:py-10">
              <div>
                <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-4 py-2 text-[11px] font-medium uppercase tracking-[0.24em] text-white/80">
                  <FaUserShield />
                  <span>Admin Panel</span>
                </div>

                <h1 className="text-3xl font-semibold tracking-tight sm:text-5xl">
                  Benton Bookings Database
                </h1>

                <p className="mt-4 max-w-3xl text-sm leading-7 text-white/80 sm:text-base lg:text-lg">
                  View bookings and manage available shows. Drag bookings or shows
                  into the recycle bin in the bottom right to remove them.
                </p>
              </div>

              <div className="rounded-[24px] border border-white/10 bg-white/[0.08] p-4 backdrop-blur-xl sm:p-5">
                <div className="mb-4 text-sm font-medium text-white/90">
                  Database summary
                </div>

                <div className="grid gap-3 sm:grid-cols-3">
                  <div className="rounded-2xl border border-white/10 bg-white/[0.08] p-4">
                    <div className="text-xs uppercase tracking-[0.24em] text-white/55">
                      Total bookings
                    </div>
                    <div className="mt-2 text-xl font-semibold text-white">
                      {rows.length}
                    </div>
                  </div>

                  <div className="rounded-2xl border border-white/10 bg-white/[0.08] p-4">
                    <div className="text-xs uppercase tracking-[0.24em] text-white/55">
                      Visible records
                    </div>
                    <div className="mt-2 text-xl font-semibold text-white">
                      {filteredRows.length}
                    </div>
                  </div>

                  <div className="rounded-2xl border border-white/10 bg-white/[0.08] p-4">
                    <div className="text-xs uppercase tracking-[0.24em] text-white/55">
                      Shows
                    </div>
                    <div className="mt-2 text-xl font-semibold text-white">
                      {shows.length}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="mb-8 rounded-[30px] border border-white/10 bg-[rgba(17,26,43,0.72)] p-4 shadow-[0_18px_60px_rgba(0,0,0,0.30)] backdrop-blur-xl sm:p-6 lg:p-7">
            <div className="mb-5 flex items-center justify-between gap-4">
              <div>
                <div className="text-[11px] uppercase tracking-[0.24em] text-white/50">
                  Show management
                </div>
                <h2 className="mt-1 text-2xl font-semibold tracking-tight sm:text-3xl">
                  Shows
                </h2>
              </div>

              <button
                type="button"
                onClick={openCreateShowModal}
                className="inline-flex h-12 items-center justify-center gap-3 rounded-2xl bg-[linear-gradient(90deg,#6a1b9a,#3c67d6,#1aa4b8)] px-5 text-sm font-semibold text-white shadow-[0_12px_32px_rgba(26,164,184,0.25)] transition hover:brightness-110"
              >
                <FaPlus />
                <span>Create Show</span>
              </button>
            </div>

            {showsLoading ? (
              <div className="grid min-h-[160px] place-items-center rounded-[24px] border border-white/10 bg-white/[0.04] text-sm text-white/70">
                Loading shows...
              </div>
            ) : (
              <div className="overflow-x-auto">
                <div className="min-w-[980px] space-y-3">
                  <div className="grid grid-cols-[2fr_1fr_1fr_1fr_1.9fr] gap-4 px-4 text-xs uppercase tracking-[0.24em] text-white/45">
                    <div>Show Name</div>
                    <div>Date</div>
                    <div>Time</div>
                    <div>Enabled</div>
                    <div>Actions</div>
                  </div>

                  {shows.length === 0 ? (
                    <div className="rounded-[24px] border border-white/10 bg-white/[0.04] px-5 py-8 text-center text-sm text-white/70">
                      No shows created yet.
                    </div>
                  ) : (
                    shows.map((show) => (
                      <div
                        key={show.id}
                        draggable
                        onDragStart={handleDragStart(
                          { type: "show", data: show },
                          `Show: ${show.show_name}`
                        )}
                        onDragEnd={handleDragEnd}
                        className="grid cursor-grab grid-cols-[2fr_1fr_1fr_1fr_1.9fr] items-center gap-4 rounded-[24px] border border-white/10 bg-white/[0.04] px-4 py-4 transition hover:border-cyan-300/20 hover:bg-white/[0.06] active:cursor-grabbing"
                      >
                        <div className="text-sm font-medium text-white">
                          {show.show_name}
                        </div>
                        <div className="text-sm text-white">
                          {formatDisplayDate(show.show_date)}
                        </div>
                        <div className="text-sm text-white">
                          {formatTimeDisplay(show.show_time)}
                        </div>
                        <div>
                          <span
                            className={`inline-flex rounded-full border px-3 py-1 text-xs font-medium ${
                              show.enabled
                                ? "border-emerald-400/20 bg-emerald-500/10 text-emerald-100"
                                : "border-white/10 bg-white/5 text-white/70"
                            }`}
                          >
                            {show.enabled ? "Enabled" : "Disabled"}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => handleToggleShow(show)}
                            className="rounded-xl border border-white/10 bg-white/[0.06] px-3 py-2 text-sm text-white transition hover:bg-white/[0.10]"
                          >
                            {show.enabled ? "Disable" : "Enable"}
                          </button>
                          <button
                            type="button"
                            onClick={() => openEditShowModal(show)}
                            className="inline-flex items-center gap-2 rounded-xl border border-white/10 bg-white/[0.06] px-3 py-2 text-sm text-white transition hover:bg-white/[0.10]"
                          >
                            <FaEdit />
                            <span>Edit</span>
                          </button>
                          <button
                            type="button"
                            onClick={() => openCancelShowModal(show)}
                            className="rounded-xl border border-amber-300/20 bg-amber-500/10 px-3 py-2 text-sm text-amber-100 transition hover:bg-amber-500/20"
                          >
                            Cancel
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDeleteShow(show)}
                            disabled={savingCode === `show-${show.id}`}
                            className="inline-flex items-center gap-2 rounded-xl border border-red-400/20 bg-red-500/10 px-3 py-2 text-sm text-red-100 transition hover:bg-red-500/20 disabled:opacity-60"
                          >
                            <FaTrash />
                            <span>
                              {savingCode === `show-${show.id}` ? "Moving..." : "Delete"}
                            </span>
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}
          </section>

          <section className="rounded-[30px] border border-white/10 bg-[rgba(17,26,43,0.72)] p-4 shadow-[0_18px_60px_rgba(0,0,0,0.30)] backdrop-blur-xl sm:p-6 lg:p-7">
            <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              <div className="relative w-full max-w-[420px]">
                <FaSearch className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-white/45" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search email, username, booking code, date or seats"
                  className="h-12 w-full rounded-2xl border border-white/10 bg-white/[0.08] pl-12 pr-4 text-sm text-white outline-none transition placeholder:text-white/35 focus:border-cyan-300/40"
                />
              </div>

              <button
                type="button"
                onClick={fetchBookings}
                className="inline-flex h-12 items-center justify-center gap-3 rounded-2xl bg-[linear-gradient(90deg,#6a1b9a,#3c67d6,#1aa4b8)] px-5 text-sm font-semibold text-white shadow-[0_12px_32px_rgba(26,164,184,0.25)] transition hover:brightness-110"
              >
                <FaSyncAlt />
                <span>Refresh</span>
              </button>
            </div>

            {pageError ? (
              <div className="mb-4 flex items-start gap-3 rounded-2xl border border-red-400/20 bg-red-500/10 px-4 py-4 text-sm text-red-100">
                <FaExclamationTriangle className="mt-0.5 shrink-0" />
                <span>{pageError}</span>
              </div>
            ) : null}

            {toast ? (
              <div className="mb-4 flex items-start gap-3 rounded-2xl border border-emerald-400/20 bg-emerald-500/10 px-4 py-4 text-sm text-emerald-100">
                <FaCheckCircle className="mt-0.5 shrink-0" />
                <span>{toast}</span>
              </div>
            ) : null}

            {loading ? (
              <div className="grid min-h-[260px] place-items-center rounded-[24px] border border-white/10 bg-white/[0.04] text-sm text-white/70">
                Loading bookings...
              </div>
            ) : (
              <div className="overflow-x-auto">
                <div className="min-w-[1650px]">
                  <div className="mb-3 grid grid-cols-[2.1fr_1.4fr_0.8fr_1.6fr_1fr_1.6fr_1fr] gap-4 px-4 text-xs uppercase tracking-[0.24em] text-white/45">
                    <div>Email</div>
                    <div>Roblox Username</div>
                    <div>Capacity</div>
                    <div>Show</div>
                    <div>Booking Code</div>
                    <div>Status</div>
                    <div>Delete</div>
                  </div>

                  <div className="space-y-3">
                    {filteredRows.length === 0 ? (
                      <div className="rounded-[24px] border border-white/10 bg-white/[0.04] px-5 py-8 text-center text-sm text-white/70">
                        No bookings found.
                      </div>
                    ) : (
                      filteredRows.map((booking) => (
                        <div
                          key={booking.booking_reference}
                          draggable
                          onDragStart={handleDragStart(
                            { type: "booking", data: booking },
                            `Booking: ${booking.booking_reference}`
                          )}
                          onDragEnd={handleDragEnd}
                          className="grid cursor-grab grid-cols-[2.1fr_1.4fr_0.8fr_1.6fr_1fr_1.6fr_1fr] items-center gap-4 rounded-[24px] border border-white/10 bg-white/[0.04] px-4 py-4 backdrop-blur-xl transition hover:border-cyan-300/20 hover:bg-white/[0.06] active:cursor-grabbing"
                        >
                          <div className="min-w-0">
                            <div className="truncate text-sm font-medium text-white">
                              {booking.email}
                            </div>
                          </div>

                          <div className="min-w-0">
                            <div className="truncate text-sm font-medium text-white">
                              {booking.roblox_username}
                            </div>
                            <div className="mt-1 truncate text-xs text-white/45">
                              Seats: {booking.seats.join(", ")}
                            </div>
                          </div>

                          <div>
                            <div className="text-sm font-semibold text-white">
                              {booking.capacity}
                            </div>
                          </div>

                          <div className="min-w-0">
                            <div className="truncate text-sm font-medium text-white">
                              {booking.show_name || "Unknown Show"}
                            </div>
                            <div className="mt-1 truncate text-xs text-white/45">
                              {formatDisplayDate(booking.show_date)} •{" "}
                              {formatTimeDisplay(booking.show_time)}
                            </div>
                          </div>

                          <div>
                            <div className="inline-flex items-center rounded-full border border-white/10 bg-white/[0.06] px-3 py-1 text-sm font-semibold text-white">
                              {booking.booking_reference}
                            </div>
                          </div>

                          <div>
                            <div className="flex items-center gap-3">
                              <span
                                className={`inline-flex whitespace-nowrap rounded-full border px-3 py-1 text-xs font-medium ${getStatusBadgeClasses(
                                  booking.ticket_status
                                )}`}
                              >
                                {booking.ticket_status}
                              </span>

                              <select
                                value={booking.ticket_status}
                                onChange={(e) =>
                                  handleStatusChange(booking, e.target.value)
                                }
                                disabled={savingCode === booking.booking_reference}
                                className="h-10 min-w-[140px] rounded-xl border border-white/10 bg-[#142138] px-3 text-sm text-white outline-none focus:border-cyan-300/40 disabled:opacity-60"
                              >
                                {STATUS_OPTIONS.map((status) => (
                                  <option key={status} value={status}>
                                    {status}
                                  </option>
                                ))}
                              </select>
                            </div>
                          </div>

                          <div>
                            <button
                              type="button"
                              onClick={() => handleDeleteBooking(booking)}
                              disabled={savingCode === booking.booking_reference}
                              className="inline-flex items-center gap-2 rounded-xl border border-red-400/20 bg-red-500/10 px-3 py-2 text-sm text-red-100 transition hover:bg-red-500/20 disabled:opacity-60"
                            >
                              <FaTrash />
                              <span>
                                {savingCode === booking.booking_reference
                                  ? "Moving..."
                                  : "Delete"}
                              </span>
                            </button>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
            )}
          </section>
        </div>

        {showModalOpen ? (
          <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/60 px-4">
            <div className="w-full max-w-[560px] rounded-[28px] border border-white/10 bg-[#101a2b] p-6 shadow-[0_24px_80px_rgba(0,0,0,0.45)]">
              <div className="mb-5">
                <div className="text-[11px] uppercase tracking-[0.24em] text-white/50">
                  Show management
                </div>
                <h3 className="mt-1 text-2xl font-semibold text-white">
                  {editingShow ? "Edit Show" : "Create Show"}
                </h3>
              </div>

              <form onSubmit={handleSaveShow} className="space-y-4">
                <div>
                  <label className="mb-2 block text-sm font-medium text-white/82">
                    Show Name
                  </label>
                  <input
                    type="text"
                    name="show_name"
                    value={showForm.show_name}
                    onChange={handleShowFormChange}
                    placeholder="Enter show name"
                    className="h-12 w-full rounded-2xl border border-white/10 bg-white/[0.08] px-4 text-sm text-white outline-none transition placeholder:text-white/35 focus:border-cyan-300/40"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-white/82">
                    Date
                  </label>
                  <input
                    type="date"
                    name="show_date"
                    value={showForm.show_date}
                    onChange={handleShowFormChange}
                    className="h-12 w-full rounded-2xl border border-white/10 bg-white/[0.08] px-4 text-sm text-white outline-none transition focus:border-cyan-300/40"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-white/82">
                    Time of Showing
                  </label>
                  <input
                    type="time"
                    name="show_time"
                    value={showForm.show_time}
                    onChange={handleShowFormChange}
                    className="h-12 w-full rounded-2xl border border-white/10 bg-white/[0.08] px-4 text-sm text-white outline-none transition focus:border-cyan-300/40"
                  />
                </div>

                <div className="flex items-center justify-end gap-3 pt-2">
                  <button
                    type="button"
                    onClick={closeShowModal}
                    className="rounded-2xl border border-white/10 bg-white/[0.06] px-4 py-3 text-sm font-medium text-white transition hover:bg-white/[0.10]"
                  >
                    Cancel
                  </button>

                  <button
                    type="submit"
                    disabled={showFormLoading}
                    className="rounded-2xl bg-[linear-gradient(90deg,#6a1b9a,#3c67d6,#1aa4b8)] px-4 py-3 text-sm font-semibold text-white transition hover:brightness-110 disabled:opacity-60"
                  >
                    {showFormLoading
                      ? editingShow
                        ? "Saving..."
                        : "Creating..."
                      : editingShow
                        ? "Save Changes"
                        : "Create Show"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        ) : null}

        {cancelShowModalOpen && showToCancel ? (
          <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/70 px-4">
            <div className="w-full max-w-[620px] rounded-[28px] border border-red-400/20 bg-[#101a2b] p-6 shadow-[0_24px_80px_rgba(0,0,0,0.50)]">
              <div className="mb-5">
                <div className="text-[11px] uppercase tracking-[0.24em] text-red-200/70">
                  Show cancellation
                </div>
                <h3 className="mt-1 text-2xl font-semibold text-white">
                  Are you sure you want to cancel {showToCancel.show_name}?
                </h3>
              </div>

              <div className="rounded-[22px] border border-white/10 bg-white/[0.05] p-4">
                <div className="mb-3 text-sm font-medium text-white/90">
                  Show details
                </div>

                <ul className="space-y-2 text-sm text-white/75">
                  <li>
                    <span className="font-medium text-white">Show Name:</span>{" "}
                    {showToCancel.show_name}
                  </li>
                  <li>
                    <span className="font-medium text-white">Date:</span>{" "}
                    {formatDisplayDate(showToCancel.show_date)}
                  </li>
                  <li>
                    <span className="font-medium text-white">Time:</span>{" "}
                    {formatTimeDisplay(showToCancel.show_time)}
                  </li>
                  <li>
                    <span className="font-medium text-white">Status:</span>{" "}
                    {showToCancel.enabled ? "Enabled" : "Disabled"}
                  </li>
                </ul>
              </div>

              <div className="mt-5 rounded-[22px] border border-amber-300/20 bg-amber-500/10 px-4 py-4 text-sm text-amber-100">
                This will delete the show, remove its bookings automatically, and
                email all booked attendees with a show cancellation notice.
              </div>

              <div className="mt-6 flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={closeCancelShowModal}
                  disabled={cancelShowLoading}
                  className="rounded-2xl border border-white/10 bg-white/[0.06] px-5 py-3 text-sm font-medium text-white transition hover:bg-white/[0.10] disabled:opacity-60"
                >
                  No
                </button>

                <button
                  type="button"
                  onClick={handleCancelShow}
                  disabled={cancelShowLoading}
                  className="rounded-2xl border border-red-400/20 bg-red-500/10 px-5 py-3 text-sm font-semibold text-red-100 transition hover:bg-red-500/20 disabled:opacity-60"
                >
                  {cancelShowLoading ? "Cancelling..." : "Yes"}
                </button>
              </div>
            </div>
          </div>
        ) : null}

        <div className="fixed bottom-6 right-6 z-[120] flex flex-col items-end gap-2">
          {draggingLabel ? (
            <div className="rounded-2xl border border-white/10 bg-[#101a2b]/90 px-4 py-2 text-xs text-white/80 shadow-[0_18px_40px_rgba(0,0,0,0.35)] backdrop-blur-xl">
              Dragging: {draggingLabel}
            </div>
          ) : null}

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => navigate("/recyclebin")}
              onDragOver={handleBinDragOver}
              onDragLeave={handleBinDragLeave}
              onDrop={handleBinDrop}
              className={`group flex h-16 w-16 items-center justify-center rounded-full border text-white shadow-[0_18px_40px_rgba(0,0,0,0.35)] backdrop-blur-xl transition ${
                isBinHovering
                  ? "scale-110 border-red-300/40 bg-red-500/20"
                  : "border-white/10 bg-[#101a2b]/90 hover:scale-105 hover:bg-[#15233a]/95"
              }`}
              title="Recycle Bin"
            >
              {isBinHovering ? (
                <FaTrash className="text-2xl text-red-100" />
              ) : (
                <FaTrashRestore className="text-2xl text-white" />
              )}
            </button>
          </div>

          <div className="rounded-2xl border border-white/10 bg-[#101a2b]/90 px-4 py-2 text-xs text-white/75 shadow-[0_18px_40px_rgba(0,0,0,0.35)] backdrop-blur-xl">
            Drop here to recycle • Click to open bin
          </div>
        </div>
      </div>
    </>
  );
}